import type { Row, SessionSimulation } from '../types'
import { normalizeText, parseList } from './csv'

const questionFiles = [
  'questions_multiple_choice.csv',
  'questions_true_false.csv',
  'questions_ordering.csv',
  'questions_matching.csv',
  'questions_sources.csv',
  'questions_development.csv',
]

export function allQuestions(files: Record<string, Row[]>): Row[] {
  return questionFiles.flatMap((fileName) => (files[fileName] ?? []).map((row) => ({ ...row, _fileName: fileName })))
}

function recentIds(sessions: Row[], currentId: string): Set<string> {
  const recent = sessions.filter((session) => session.id !== currentId).slice(-3)
  return new Set(recent.flatMap((session) => parseList(session.simulated_question_ids || session.question_ids || '')))
}

function rankDifficulty(question: Row, preferred: string): number {
  const order = ['Adaptada', 'Básica', 'Intermedia', 'Avanzada', 'Desafío']
  const index = order.indexOf(question.difficulty || '')
  const target = order.indexOf(preferred)
  if (index < 0 || target < 0) return 10
  return Math.abs(index - target)
}

export function simulateSession(session: Row, files: Record<string, Row[]>, sessions: Row[]): SessionSimulation {
  const requestedCount = Math.max(1, Math.min(30, Number(session.question_count || 10)))
  const selectedOAs = parseList(session.selected_oas || '').map(normalizeText)
  const selectedTopics = parseList(session.topics || '').map(normalizeText)
  const selectedUnit = normalizeText(session.unit || '')
  const recent = recentIds(sessions, session.id || '')
  const excluded: Array<{ id: string; reason: string }> = []

  const eligible = allQuestions(files).filter((question) => {
    const reasons: string[] = []
    if (selectedUnit && normalizeText(question.unit || '') !== selectedUnit) reasons.push('Otra unidad')
    if (selectedOAs.length && !selectedOAs.includes(normalizeText(question.oa || ''))) reasons.push('OA no seleccionado')
    if (selectedTopics.length && !selectedTopics.includes(normalizeText(question.topic || ''))) reasons.push('Tema no seleccionado')
    if (['false', '0', 'no'].includes(normalizeText(question.active || 'true'))) reasons.push('Pregunta inactiva')
    if (question.status === 'Archivado') reasons.push('Pregunta archivada')
    if (question.image_path && question.image_status === 'Pendiente de subir a GitHub') reasons.push('Imagen pendiente')
    if (reasons.length) excluded.push({ id: question.id, reason: reasons.join(', ') })
    return !reasons.length
  })

  const scored = eligible.map((question, index) => ({
    question,
    score: rankDifficulty(question, session.difficulty || 'Intermedia') * 100
      + (recent.has(question.id) ? 50 : 0)
      + index / 1000,
  })).sort((a, b) => a.score - b.score)

  const selected: Row[] = []
  const targetCount = Math.min(requestedCount, scored.length)
  const topicCounts = new Map<string, number>()
  while (selected.length < targetCount) {
    let bestIndex = 0
    let bestValue = Number.POSITIVE_INFINITY
    scored.forEach((candidate, index) => {
      const topic = candidate.question.topic || ''
      const balancePenalty = (topicCounts.get(topic) || 0) * 20
      const value = candidate.score + balancePenalty
      if (value < bestValue) { bestValue = value; bestIndex = index }
    })
    const [{ question }] = scored.splice(bestIndex, 1)
    selected.push(question)
    topicCounts.set(question.topic || '', (topicCounts.get(question.topic || '') || 0) + 1)
  }

  const difficultyCounts: Record<string, number> = {}
  const typeCounts: Record<string, number> = {}
  selected.forEach((question) => {
    difficultyCounts[question.difficulty || 'Sin definir'] = (difficultyCounts[question.difficulty || 'Sin definir'] || 0) + 1
    typeCounts[question.type || 'Sin definir'] = (typeCounts[question.type || 'Sin definir'] || 0) + 1
  })

  const warnings: string[] = []
  if (selected.length < requestedCount) warnings.push(`Solo existen ${selected.length} preguntas compatibles. La cantidad se reducirá automáticamente.`)
  if (recent.size && selected.some((question) => recent.has(question.id))) warnings.push('Fue necesario reutilizar preguntas de las últimas tres sesiones.')
  const maxTopic = Math.max(0, ...Object.values(Object.fromEntries(topicCounts)))
  if (selected.length >= 4 && maxTopic > Math.ceil(selected.length / 2)) warnings.push('Más de la mitad de la sesión pertenece al mismo tema.')

  return { requestedCount, effectiveCount: selected.length, questions: selected, excluded, difficultyCounts, typeCounts, warnings }
}

export function reducedVersion(session: Row, simulation: SessionSimulation): SessionSimulation {
  const target = Math.min(simulation.effectiveCount, Number(session.reduced_question_count || Math.max(1, Math.round(simulation.effectiveCount * 0.75))))
  const order = ['Adaptada', 'Básica', 'Intermedia', 'Avanzada', 'Desafío']
  const questions = [...simulation.questions].sort((a, b) => order.indexOf(a.difficulty || 'Intermedia') - order.indexOf(b.difficulty || 'Intermedia')).slice(0, target)
  const difficultyCounts: Record<string, number> = {}
  const typeCounts: Record<string, number> = {}
  questions.forEach((question) => {
    difficultyCounts[question.difficulty || 'Sin definir'] = (difficultyCounts[question.difficulty || 'Sin definir'] || 0) + 1
    typeCounts[question.type || 'Sin definir'] = (typeCounts[question.type || 'Sin definir'] || 0) + 1
  })
  return { ...simulation, requestedCount: target, effectiveCount: questions.length, questions, difficultyCounts, typeCounts }
}
